// src/sim/ai/conditions.ts — BT leaf condition library for hunter (and future role) trees.
//
// All functions are called with `this` bound to an AgentContext object by mistreevous.
// Each condition returns a boolean: true = SUCCEEDED, false = FAILED in the BT.
//
// Conditions MUST only read from `this.perception` (Pitfall P4-C: `this.world` is `never`).
//
// Source: .planning/phases/04-ai/04-05-PLAN.md Task 1 action;
//         04-CONTEXT.md D-09, D-12, D-34.

import type { AgentContext } from './ai-controller-types';
import type { EventTag } from '../../shared/types/events';
import {
  P4_DOG_FOV_RADIUS_TILES,
  P4_DOG_CALM_RADIUS_TILES,
  P4_DOG_BARK_COOLDOWN_TICKS,
  P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS,
} from './constants';
import { hasProfile, getProfile } from '../characters/profiles';

/**
 * IsWinningBranch(branchId) — utility-selector guard (Pattern 3).
 *
 * Returns true iff `this.winningBranch === branchId`.
 * AIController.decide() sets winningBranch via argmaxBranch() before tree.step(),
 * so exactly one branch's guard succeeds per step.
 *
 * mistreevous `while` semantics: re-evaluated on every leaf-node update while the
 * branch is in RUNNING. If the winning branch flips between decide-ticks, the
 * while guard fails, the current sequence transitions to FAILED, the parent
 * selector advances — no tree.reset() needed.
 */
export function IsWinningBranch(this: AgentContext, branchId: string): boolean {
  return this.winningBranch === branchId;
}

/**
 * HasVisibleHider — true iff at least one entity with role='hider' is in perception.visible.
 * Guards the 'chase' branch; prevents chase from running with an empty visible list (Pitfall 4).
 */
export function HasVisibleHider(this: AgentContext): boolean {
  return this.perception.visible.some(v => v.role === 'hider');
}

/**
 * HasBlackboardEntry — true iff the observer's memory has at least one entry.
 * Guards the 'investigate-sighting' branch; prevents wasted pathing when memory is empty.
 */
export function HasBlackboardEntry(this: AgentContext): boolean {
  return this.perception.memory.length > 0;
}

/**
 * HasUncheckedSpots — true iff there is at least one hiding spot not yet in checkedHidingSpotIds.
 * Guards the 'check-hiding-spot' branch.
 * checkedHidingSpotIds is a Set injected by AIController; reset each round (D-34).
 */
export function HasUncheckedSpots(this: AgentContext): boolean {
  const all = this.perception.map.hidingSpots ?? [];
  if (all.length === 0) return false;
  return all.some(s => !this.checkedHidingSpotIds.has(s.id));
}

// ---------------------------------------------------------------------------
// Hider-specific conditions (Plan 06)
// ---------------------------------------------------------------------------

/**
 * HasCaughtTeammatesAtBase — true iff at least one teammate is caught at base.
 * Guards the 'liberate' branch in hider.bt.mdsl.
 * Reads from caughtAtBaseCount scratch field on the agent, set by AIController
 * from perception each decide tick.
 * TODO(Plan 10): wire caughtAtBaseCount via perception extension.
 */
export function HasCaughtTeammatesAtBase(this: AgentContext & { caughtAtBaseCount?: number }): boolean {
  return (this.caughtAtBaseCount ?? 0) > 0;
}

/**
 * HighFearAndOnFloorAbility — true iff fear > P4_HIDER_HIGH_FEAR_THRESHOLD.
 * The actual on-floor ability check is enforced in the DropToFloor action
 * (action returns FAILED when profile lacks the ability). Guards the 'fallback-drop' branch.
 * Source: FEAR-06, D-18.
 */
export function HighFearAndOnFloorAbility(this: AgentContext): boolean {
  return this.perception.self.fear > 0.5;
}

// ---------------------------------------------------------------------------
// Dog conditions (D-24, D-25, NPC-07)
// ---------------------------------------------------------------------------

/**
 * HasDetection — true iff any entity in perception.visible is within P4_DOG_FOV_RADIUS_TILES.
 * Dogs use simple radius detection (NOT rot-js FOV) per D-24 / Pitfall #12.
 * Dogs detect through walls by design ("dogs hear through walls by barking design" — D-24).
 * NOTE: Plan 10 must ensure perception.visible for dogs includes ALL entities within radius
 * regardless of light gate (D-24 "dogs hear through walls by barking design").
 * TODO(Plan 10): special-case dog perception to skip light gate.
 */
export function HasDetection(this: AgentContext & { dogBarkLastTick?: number }): boolean {
  const selfPos = this.perception.self.position;
  const radiusPx = P4_DOG_FOV_RADIUS_TILES * this.perception.map.tileWidth;
  for (const v of this.perception.visible) {
    const d = Math.hypot(v.position.x - selfPos.x, v.position.y - selfPos.y);
    if (d <= radiusPx) return true;
  }
  return false;
}

/**
 * NotSuppressedByCalmHolder — false iff any entity with 'calm-dog' ability in profile
 * is within P4_DOG_CALM_RADIUS_TILES of the dog. D-25, NPC-07.
 * Ze and Mary (characterId 'ze', 'mary') both carry 'calm-dog' ability.
 * Suppression is instantaneous (no accumulator) — D-25.
 */
export function NotSuppressedByCalmHolder(this: AgentContext): boolean {
  const selfPos = this.perception.self.position;
  const radiusPx = P4_DOG_CALM_RADIUS_TILES * this.perception.map.tileWidth;
  for (const v of this.perception.visible) {
    if (!hasProfile(v.id)) continue;
    const profile = getProfile(v.id);
    if (profile.abilities.includes('calm-dog')) {
      const d = Math.hypot(v.position.x - selfPos.x, v.position.y - selfPos.y);
      if (d <= radiusPx) return false; // suppressed
    }
  }
  return true;
}

/**
 * BarkCooldownElapsed — true iff P4_DOG_BARK_COOLDOWN_TICKS have passed since last bark.
 * dogBarkLastTick is undefined before first bark (treated as -Infinity → always elapsed).
 */
export function BarkCooldownElapsed(this: AgentContext & { dogBarkLastTick?: number }): boolean {
  const last = this.dogBarkLastTick ?? -Infinity;
  return this.perception.tick - last >= P4_DOG_BARK_COOLDOWN_TICKS;
}

// ---------------------------------------------------------------------------
// Adult conditions (D-28, NPC-04)
// ---------------------------------------------------------------------------

/**
 * HasCommentableEventWithinCooldown — true iff global commentary cooldown elapsed
 * AND there are pending commentable events. D-28, NPC-04.
 * lastAdultCommentaryTick is undefined before first commentary.
 * pendingCommentableEvents wired in Plan 10; stub checks for non-empty array.
 */
export function HasCommentableEventWithinCooldown(
  this: AgentContext & {
    lastAdultCommentaryTick?: number | null;
    pendingCommentableEvents?: Array<{ eventTag: EventTag; tick: number; type: string }>;
  },
): boolean {
  const last = this.lastAdultCommentaryTick ?? null;
  if (last !== null && (this.perception.tick - last) < P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS) {
    return false;
  }
  return (this.pendingCommentableEvents?.length ?? 0) > 0;
}
